import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { BookOpen, MessageSquare, Send, Diamond } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WeeklyScripture = () => {
  const { user } = useAuth();
  const [scripture, setScripture] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    fetchScripture();
  }, []);

  useEffect(() => {
    if (scripture?.id && showComments) {
      fetchComments();
    }
  }, [scripture?.id, showComments]);

  const fetchScripture = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/scripture/current`);
      setScripture(response.data);
    } catch (error) {
      console.error('Failed to fetch scripture:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    if (!scripture?.id) return;
    try {
      const response = await axios.get(`${API_URL}/api/reflection/${scripture.id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const handlePostComment = async () => {
    if (!newComment.trim() || !scripture?.id) return;

    setPosting(true);
    try {
      const response = await axios.post(`${API_URL}/api/reflection/${scripture.id}/comments`, {
        content: newComment
      });
      setComments([...comments, response.data]);
      setNewComment('');
      toast.success('Comment posted! +5 diamonds earned');
    } catch (error) {
      toast.error('Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  if (loading) {
    return (
      <Card className="card-base mb-8 animate-pulse">
        <CardContent className="p-6">
          <div className="h-6 bg-sand rounded w-1/3 mb-4" />
          <div className="h-24 bg-sand rounded" />
        </CardContent>
      </Card>
    );
  }

  if (!scripture) return null;

  return (
    <Card className="card-base mb-8" data-testid="weekly-scripture">
      <CardHeader className="pb-2">
        <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-terracotta" />
          Weekly Soul Food
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Scripture Verse */}
        <div className="p-6 bg-gradient-to-br from-forest/5 to-terracotta/5 rounded-xl border border-forest/10 mb-4">
          <p className="text-lg font-serif italic text-forest leading-relaxed">
            "{scripture.verse}"
          </p>
          <p className="text-sm text-terracotta font-medium mt-3">
            — {scripture.reference}
          </p>
          {scripture.reflection && (
            <p className="text-sm text-gray-600 mt-4 pt-4 border-t border-forest/10">
              {scripture.reflection}
            </p>
          )}
        </div>

        {/* Comments Toggle */}
        <Button
          variant="outline"
          className="btn-outline w-full"
          onClick={() => setShowComments(!showComments)}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          {showComments ? 'Hide' : 'View'} Comments ({comments.length})
        </Button>

        {/* Comments Section */}
        {showComments && (
          <div className="mt-4 space-y-4">
            {/* Comment Input */}
            <div className="flex gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={user?.profile_image} />
                <AvatarFallback className="bg-forest text-white">
                  {user?.full_name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Share your thoughts on this week's scripture..."
                  className="input-base min-h-20 resize-none"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-purple-600 flex items-center gap-1">
                    <Diamond className="h-3 w-3" />
                    Earn 5 diamonds for commenting
                  </span>
                  <Button 
                    className="btn-primary"
                    size="sm"
                    onClick={handlePostComment}
                    disabled={posting || !newComment.trim()}
                  >
                    {posting ? 'Posting...' : (
                      <>
                        <Send className="h-4 w-4 mr-1" />
                        Post
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>

            {/* Comments List */}
            {comments.length > 0 && (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {comments.map((comment) => (
                  <div key={comment.id} className="flex gap-3 p-3 bg-sand/30 rounded-lg">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={comment.user?.profile_image} />
                      <AvatarFallback className="bg-forest text-white text-xs">
                        {comment.user?.full_name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-forest text-sm">
                          {comment.user?.full_name}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(comment.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">{comment.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {comments.length === 0 && (
              <p className="text-center text-gray-500 py-4">
                Be the first to share your thoughts!
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WeeklyScripture;
